import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";

export type TriState = "all" | "some" | "none";

interface CheckboxTriStateProps {
  state: TriState;
  disabled?: boolean;
  label: string;
  onChange: () => void;
  className?: string;
}

/**
 * Tri-State Checkbox Component
 * A checkbox that can represent three states: all selected, some selected, or none selected
 * Useful for bulk selection scenarios where items can be partially selected
 */
export function CheckboxTriState({
  state,
  disabled,
  label,
  onChange,
  className,
}: CheckboxTriStateProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);

  const checked = state === "all";
  const ariaChecked: boolean | "mixed" = state === "some" ? "mixed" : state === "all";

  return (
    <div className={cn("relative", className)}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-checked={ariaChecked}
        aria-label={label}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "appearance-none h-4 w-4 rounded border focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          state === "all" ? "bg-primary border-primary" : "bg-background border-border"
        )}
      />

      {state === "all" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <svg
            className="w-3 h-3 text-primary-foreground"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline points="20,6 9,17 4,12" />
          </svg>
        </div>
      )}

      {state === "some" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-2 h-0.5 bg-muted-foreground" />
        </div>
      )}
    </div>
  );
}

